import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import SectionHead from '@/components/ui/SectionHead'
import { useSuspenseLearningPath } from '@/features/courses/api/queries'
import { cn } from '@/lib/utils'
import { LEARN_PAGE } from '@/screens/courses/components/learn/learn-page'
import LearnHead from '@/screens/courses/components/learn/LearnHead'
import NextStepCard from '@/screens/courses/components/path/NextStepCard'
import ObjectiveOnHomeRow from '@/screens/courses/components/path/ObjectiveOnHomeRow'
import PathSkeleton from '@/screens/courses/components/path/PathSkeleton'
import PathStepsPanel from '@/screens/courses/components/path/PathStepsPanel'
import PathSummaryCard from '@/screens/courses/components/path/PathSummaryCard'
import WatchAndTalk from '@/screens/courses/components/path/WatchAndTalk'
import { loadLearningPath } from '@/screens/courses/LearningPathScreen.loader'

const TITLE = 'Objectives'

/**
 * rest:ln (mock `lo-head`): the page's own title, "Your path", under the strip that names the
 * section — a visible `h1`, unlike the hub pages whose strip tab is their title.
 */
const HEADER = <LearnHead title="Your path" />

function LearningPathPending() {
  return (
    <ScreenLayout
      {...LEARN_PAGE}
      pending
      pendingLabel="Loading your objectives…"
      header={HEADER}
      pageTitle={TITLE}
    >
      <PathSkeleton />
    </ScreenLayout>
  )
}

function LearningPathError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage {...props} fullWidth header={HEADER} title="Your objectives did not load" />
  )
}

export const Route = createFileRoute('/_app/courses/path')({
  loader: loadLearningPath,
  pendingComponent: LearningPathPending,
  errorComponent: LearningPathError,
  component: LearningPathScreen,
})

/**
 * Learn · Objectives — the one active learning path (`GET /path/`),
 * member-aware: step statuses and `showOnHome` are the signed-in member's own.
 *
 * Two cards side by side — the path with its progress, and the step to take
 * next — then every step as one panel of rows, "Watch and talk" (gated, see
 * its own file), then the Home switch. With
 * every step done there is no next step, and the path card takes the row.
 *
 * `null` (`LEARNING_PATH_NOT_FOUND`) is what dev answers today and what any
 * environment answers until a path is authored — the screen's "on its way"
 * state, not a failure (`docs/route-loading.md`, "Not-found answers"). A
 * suspense read: `useSuspenseLearningPath` always has data by the time this
 * renders, `null` included.
 */
function LearningPathScreen() {
  const { data: path } = useSuspenseLearningPath()

  if (path === null) {
    return (
      <ScreenLayout {...LEARN_PAGE} header={HEADER} pageTitle={TITLE}>
        <EmptyState
          kind="empty"
          size="page"
          title="Your learning path is on its way"
          description="A guided path through the app's own tools is coming. In the meantime, browse the course catalog."
          action={
            <Link to="/courses" className={stateActionClass('page')}>
              Browse courses
            </Link>
          }
        />
      </ScreenLayout>
    )
  }

  const next = path.steps.find((step) => step.status === 'current')

  return (
    <ScreenLayout {...LEARN_PAGE} header={HEADER} pageTitle={TITLE}>
      <div className={cn('grid grid-cols-1 gap-6', next !== undefined && '@3xl:grid-cols-2')}>
        <PathSummaryCard path={path} />
        {next === undefined ? null : <NextStepCard step={next} />}
      </div>
      <section className="flex flex-col gap-3">
        <SectionHead title="Your steps" size="card" />
        <PathStepsPanel steps={path.steps} />
      </section>
      <WatchAndTalk />
      <ObjectiveOnHomeRow showOnHome={path.showOnHome} />
    </ScreenLayout>
  )
}
